import { datadogRum } from '@datadog/browser-rum';
import { browserHttpLink } from '@seek/ca-graphql-client/browser';
import { MelwaysProvider } from '@seek/melways-react';
import { metrics, setup as setupMetrics } from '@seek/metrics-js';
import { hydrateRoot } from 'react-dom/client';

import App from 'src/components/App/App';
import datadogRumEnabled, { appName } from 'src/config';
import { isMobileUserAgent } from 'src/modules/device-detect';
import { setupLogger } from 'src/modules/logger';
import type { ClientContext } from 'src/types';
import { getCountryFromSite } from 'src/utils/melwaysUtils';
import { overrideZoneFeatures } from 'src/utils/overrideZoneFeatures';

const pathnameToPageName = (pathname: string): string => {
  if (pathname.includes('my-activity/saved-searches')) {
    return 'saved-search';
  }
  if (pathname.includes('recommended')) {
    return 'recommended';
  }
  return '';
};

export default ({ site, environment, route, language }: ClientContext) => {
  const country = getCountryFromSite(site);
  const appConfig = window.APP_CONFIG;
  const { metricsIsEnabled, metricsUrl } = appConfig;
  setupLogger();
  if (metricsIsEnabled) {
    setupMetrics({
      host: metricsUrl,
      isEnabled: metricsIsEnabled,
      prefix: `discover`,
      globalTags: [
        `country:${country}`,
        `environment:${environment}`,
        `route:${route}`,
        `device-type:${isMobileUserAgent() ? 'mobile' : 'desktop'}`,
        'service_name:disc-static-pages',
      ],
    });

    metrics.sendPagePerformance([
      `disc-static-pages:${pathnameToPageName(window.location.pathname)}`,
    ]);
  }

  const datadogRumApplicationMetadata = appConfig.datadogRumApplicationMetadata
    ? JSON.parse(appConfig.datadogRumApplicationMetadata)
    : undefined;

  if (datadogRumEnabled && datadogRumApplicationMetadata) {
    datadogRum.init({
      applicationId: datadogRumApplicationMetadata.applicationId,
      clientToken: datadogRumApplicationMetadata.clientToken,
      // `site` refers to the Datadog site parameter of your organization
      // see https://docs.datadoghq.com/getting_started/site/
      site: 'datadoghq.com',
      service: appName,
      env: 'production',
      sessionSampleRate: 1,
      sessionReplaySampleRate: 1,
      defaultPrivacyLevel: 'mask-user-input',
    });
  }

  const zoneFeatures = overrideZoneFeatures(appConfig.zoneFeatures);

  hydrateRoot(
    document.getElementById('app')!,
    <MelwaysProvider.Client site={site}>
      <App
        site={site}
        environment={environment}
        httpLink={browserHttpLink}
        route={route}
        language={language}
        {...appConfig}
        zoneFeatures={zoneFeatures}
      />
    </MelwaysProvider.Client>,
  );
};
